import React from 'react';
import { MR_EXCEL_SHORTCUTS } from '../data/shortcutsLibrary';
import { MR_EXCEL_SHORTCUT_CATEGORY_META } from '../data/shortcutsCategoryMeta';
import shortcutsDetails from '../data/shortcutsDetails.json';

// Helper to tokenise key strings
const tokeniseKeys = (value) => {
  if (!value) return [];
  let text = String(value).trim();
  text = text.replace(/(Ctrl|Shift|Alt|Fn)(?=[^\s])/g, '$1 ');
  text = text.replace(/(⌘|⇧|⌥|⌃|←|→|↑|↓)/g, ' $1 ');
  text = text.replace(/\s+/g, ' ').trim();
  return text.split(' ').filter(Boolean);
};

// Keyboard token renderer component
function KeyRenderer({ value, platform }) {
  const tokens = tokeniseKeys(value);
  if (!tokens.length) return <span className="shortcut-na">—</span>;
  return (
    <>
      {tokens.map((token, idx) => (
        <kbd key={idx} className={`shortcut-key ${platform}-key`}>
          {token}
        </kbd>
      ))}
    </>
  );
}

export default function ShortcutDetail({ slug, navigate }) {
  const shortcutItem = MR_EXCEL_SHORTCUTS.find((s) => s.slug === slug);

  if (!shortcutItem) {
    return (
      <main className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
        <h2>Shortcut not found</h2>
        <p>The requested keyboard shortcut could not be found.</p>
        <button className="btn btn-primary" onClick={() => navigate('shortcuts')}>
          Back to Shortcuts
        </button>
      </main>
    );
  }

  const meta = MR_EXCEL_SHORTCUT_CATEGORY_META[shortcutItem.category] || {};
  const details = shortcutsDetails[slug] || { windowsHtml: '', macHtml: '', exampleHtml: '' };

  const handleCategoryJump = (e) => {
    e.preventDefault();
    navigate('shortcuts');
    // Allow navigation to complete before scrolling
    const id = meta.id || shortcutItem.category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <main className="shortcut-detail" style={{ marginTop: '20px' }}>
      <div className="container">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <a href="#/shortcuts" onClick={(e) => { e.preventDefault(); navigate('shortcuts'); }}>
            Excel Shortcuts
          </a>
          <span>›</span>
          <a href="#/shortcuts" onClick={handleCategoryJump}>
            {shortcutItem.category}
          </a>
          <span>›</span>
          <span>{shortcutItem.title}</span>
        </nav>

        <div className="shortcut-detail-grid">
          <article className="shortcut-detail-card">
            <div className="shortcut-meta-row">
              <span className="tag">{shortcutItem.category}</span>
              <span className="tag">{shortcutItem.level}</span>
            </div>

            <h1>{shortcutItem.title}</h1>
            <p className="lead">
              Keyboard shortcut to {shortcutItem.title.toLowerCase()} in Excel.
            </p>

            {/* Main Key Triggers Card */}
            <section className="shortcut-triggers-panel">
              <div className="trigger-platform-box">
                <span className="platform-label">Windows</span>
                <div className="platform-keys">
                  <KeyRenderer value={shortcutItem.windows} platform="windows" />
                </div>
              </div>
              <div className="trigger-platform-box">
                <span className="platform-label">macOS</span>
                <div className="platform-keys">
                  <KeyRenderer value={shortcutItem.mac} platform="mac" />
                </div>
              </div>
            </section>

            {/* How to use steps */}
            {(details.windowsHtml || details.macHtml) && (
              <section className="detail-block">
                <h2>How to use this shortcut</h2>
                <div className="platform-steps-grid" style={{ display: 'grid', gap: '20px', gridTemplateColumns: details.windowsHtml && details.macHtml ? '1fr 1fr' : '1fr', marginTop: '14px' }}>
                  {details.windowsHtml && (
                    <div className="platform-step-card" style={{ padding: '16px', background: '#fcfcfc', border: '1px solid #eee', borderRadius: '8px' }}>
                      <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '1rem', color: '#111', borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '1.2rem' }}>⊞</span> Windows steps
                      </h3>
                      <div dangerouslySetInnerHTML={{ __html: details.windowsHtml }} />
                    </div>
                  )}
                  {details.macHtml && (
                    <div className="platform-step-card" style={{ padding: '16px', background: '#fcfcfc', border: '1px solid #eee', borderRadius: '8px' }}>
                      <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '1rem', color: '#111', borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '1.2rem' }}></span> macOS steps
                      </h3>
                      <div dangerouslySetInnerHTML={{ __html: details.macHtml }} />
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Practical example */}
            {details.exampleHtml && (
              <section className="detail-block">
                <h2>Practical workplace example</h2>
                <div 
                  className="practical-example" 
                  dangerouslySetInnerHTML={{ __html: details.exampleHtml }} 
                />
              </section>
            )}
          </article>

          <aside className="detail-aside">
            <div className="detail-aside-card">
              <h3>Category</h3>
              <p className="muted" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0' }}>
                <span className="shortcut-category-symbol" style={{ fontSize: '1.5rem', opacity: '0.8' }}>{meta.icon}</span>
                <strong>{shortcutItem.category}</strong>
              </p>
              <p className="muted">{meta.intro}</p>
              <a 
                className="btn btn-secondary btn-sm back-functions" 
                href="#/shortcuts"
                onClick={handleCategoryJump}
                style={{ marginTop: '10px', display: 'inline-block' }}
              >
                View Category
              </a>
            </div>

            <div className="detail-aside-card">
              <h3>Speed tip</h3>
              <p className="shortcut-note">
                Using shortcuts instead of mouse clicks saves up to 2 seconds per action. Over a year, this saves up to 40 hours of workplace productivity.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
